/**
 * monetization/types — model for the decoupled monetization kit (canon).
 *
 * Portfolio DEFAULT business model: the `free` tier is AD-SUPPORTED
 * (profitable with free users from day one, `adsEnabled: true`) with
 * generous-but-limited quotas, and paid tiers are ad-free with more
 * storage/quotas. Everything is configurable via env (defaults) + admin
 * UI (persisted overrides) without touching code.
 *
 * TypeScript mirror of the Rust model (`core-rust/src/models/monetization.rs` +
 * `monetization/resolve.rs`): keep BOTH in sync.
 */

/** Canonical quota keys, common to the entire portfolio. */
export const QUOTA_KEYS = ["storage_bytes", "items", "collections", "orgs", "attachments"] as const;

export type QuotaKey = (typeof QUOTA_KEYS)[number];

/** Limits for a tier. `null` = unlimited (env: -1). */
export type QuotaLimits = Record<QuotaKey, number | null>;

export interface TierDef {
  /** Stable ID ("free", "pro", …). */
  id: string;
  /** Display name. */
  label: string;
  /** true ⇒ ad-funded tier (free ad-supported). */
  adsEnabled: boolean;
  limits: QuotaLimits;
}

/** Resolved configuration (code defaults → env → UI overrides). */
export interface MonetizationConfig {
  /** Master switch; false (default) ⇒ the entire layer is inert. */
  enabled: boolean;
  paymentsEnabled: boolean;
  /** Injected PaymentProvider ID ("none" | "stripe" | …). */
  provider: string;
  /** Tier for anyone without a subscription — and for guests (guest-first). */
  defaultTier: string;
  tiers: TierDef[];
}

/** A persisted override (a `monetization_config` row edited in the admin UI). */
export interface MonetizationOverrideRow {
  key: string;
  value: unknown;
}

/** Actual server-side usage, isolated per user (CC-40). */
export type UsageSnapshot = Record<QuotaKey, number>;

/** Quota evaluation for the UI (usage bar vs limit). */
export interface QuotaEvaluation {
  key: QuotaKey;
  used: number;
  /** null = unlimited. */
  limit: number | null;
  /** 0..1 (0 if unlimited). */
  ratio: number;
  /** true from 80% of the limit onward. */
  nearLimit: boolean;
  exceeded: boolean;
}
